/**
 * Where the primary "Sign up" CTA sends people. One constant so the whole
 * site moves together if the app's signup path ever changes.
 */
export const SIGNUP_BASE_URL = "https://app.huntbase.io/auth/sign-up";

/** Where "Sign in" sends people. */
export const LOGIN_URL = "https://app.huntbase.io";

/** The public status page — hosted separately, not part of this site. */
export const STATUS_URL = "https://status.huntbase.io/";

/** The Huntbase Hub's origin, for recognising links to it. */
export const HUNT_HUB_ORIGIN = "https://hub.huntbase.io";

/**
 * The Huntbase Hub — its own property, not part of this site. Links carry
 * referral UTMs so the hub can tell website traffic from everything else.
 */
export const HUNT_HUB_URL = `${HUNT_HUB_ORIGIN}/?utm_source=huntbase-website&utm_medium=referral`;

/**
 * Where "Book a demo" sends people: the Calendly "Huntbase demo" event, which
 * has its own narrow availability window (set in Calendly, not here).
 * /contact?intent=demo still works for anyone who'd rather write in.
 */
export const BOOKING_URL = "https://calendly.com/tyleroliver/huntbase-demo";

/**
 * A recorded walkthrough of Scout running a hunt, shown on /demo. Empty means
 * no video: the page omits the player entirely.
 * TODO: set once the recording is published.
 */
export const DEMO_VIDEO_URL: string = "";

/**
 * WebVTT captions for DEMO_VIDEO_URL. The player only renders when both are
 * set: a narrated walkthrough without captions shuts out part of the audience.
 * TODO: set alongside DEMO_VIDEO_URL.
 */
export const DEMO_VIDEO_CAPTIONS_URL: string = "";

/** The open hunt.md specification. */
export const HUNT_MD_SPEC_URL = "https://github.com/huntbase-io/hunt-md";

/** The Huntbase GitHub organisation. */
export const GITHUB_URL = "https://github.com/huntbase-io";

/**
 * Public product docs. VITE_DOCS_URL overrides it (e.g. a preview docs
 * build); unset, the nav still links to the production docs.
 */
export const DOCS_URL =
  (import.meta.env.VITE_DOCS_URL as string | undefined) || "https://docs.huntbase.io";

/**
 * How someone mid-incident reaches a human fastest. Shown on /been-breached.
 * TODO: add a staffed phone number here (and render it) once one exists.
 * Never put a placeholder number on the page.
 */
export const URGENT_CONTACT = {
  email: "hello@huntbase.io",
  phone: null as string | null,
};

/**
 * The campaign a visitor arrived with. The app reads UTMs off the sign-up URL,
 * so if we stamped every link `utm_source=huntbase-website` a LinkedIn or
 * newsletter visitor would reach the app attributed to the website.
 */
export type FirstTouch = Partial<
  Record<"utm_source" | "utm_medium" | "utm_campaign" | "utm_term" | "utm_id" | "gclid", string>
>;

const FIRST_TOUCH_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_id",
  "gclid",
] as const;

/** Campaign values are echoed into a URL; keep them short and printable. */
const MAX_VALUE_LENGTH = 150;

/** Pull inbound campaign params out of a query string; null when there are none. */
export function parseFirstTouch(search: string): FirstTouch | null {
  const params = new URLSearchParams(search);
  const touch: FirstTouch = {};
  for (const key of FIRST_TOUCH_KEYS) {
    const value = params.get(key)?.trim().slice(0, MAX_VALUE_LENGTH);
    if (value) touch[key] = value;
  }
  return Object.keys(touch).length ? touch : null;
}

/**
 * First-touch campaign, captured once when this module first loads (it is in
 * the main bundle, so that is the landing URL) and held in memory for the SPA
 * session. Deliberately not written to cookies or web storage: attribution
 * must work for visitors who never give consent.
 */
const firstTouch: FirstTouch | null =
  typeof window === "undefined" ? null : parseFirstTouch(window.location.search);

export function getFirstTouch(): FirstTouch | null {
  return firstTouch;
}

/**
 * Every signup link carries UTMs so the app can tell where the visitor came
 * from, and which CTA did the work. `placement` is kept short, kebab-case and
 * stable, because it lands in analytics as `utm_content`.
 *
 * With no inbound campaign the link is attributed to the website. With one,
 * the inbound source/medium/campaign win and only `utm_content` is ours.
 */
export function signupUrl(placement: string, touch: FirstTouch | null = firstTouch): string {
  if (!touch) {
    const params = new URLSearchParams({
      utm_source: "huntbase-website",
      utm_medium: "cta",
      utm_campaign: "signup",
      utm_content: placement,
    });
    return `${SIGNUP_BASE_URL}?${params.toString()}`;
  }

  const params = new URLSearchParams();
  // A bare gclid (auto-tagged Google Ads click, no manual UTMs) still has a
  // known source; say so rather than claiming it for the website.
  const source = touch.utm_source ?? (touch.gclid ? "google" : undefined);
  const medium = touch.utm_medium ?? (touch.gclid && !touch.utm_source ? "cpc" : undefined);
  if (source) params.set("utm_source", source);
  if (medium) params.set("utm_medium", medium);
  if (touch.utm_campaign) params.set("utm_campaign", touch.utm_campaign);
  if (touch.utm_term) params.set("utm_term", touch.utm_term);
  if (touch.utm_id) params.set("utm_id", touch.utm_id);
  params.set("utm_content", placement);
  if (touch.gclid) params.set("gclid", touch.gclid);
  return `${SIGNUP_BASE_URL}?${params.toString()}`;
}

/** Default signup link, for callers with no more specific placement. */
export const SIGNUP_URL = signupUrl("site");
